import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ShieldQuestion, ExternalLink } from "lucide-react";
import { Layout } from "../components/Layout";

const Section = ({ title, children }) => (
  <div>
    <h2 className="font-serif-display text-xl font-semibold text-[#9E4535] mb-2">{title}</h2>
    <div className="font-body text-base text-[#2E282A] leading-[1.85] space-y-2">{children}</div>
  </div>
);

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Privacy Policy · CurioVault";
    return () => { document.title = "CurioVault"; };
  }, []);

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="privacy-policy-page">
        <nav className="flex items-center gap-2 font-mono-cv text-xs text-[#7A6C5D] mb-8" data-testid="breadcrumb">
          <Link to="/" className="hover:text-[#9E4535]">Vault</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#1A1615]">Privacy Policy</span>
        </nav>

        <div className="flex items-center gap-4 mb-2">
          <div className="w-12 h-12 shrink-0 rounded-full border-2 border-[#C5A059] flex items-center justify-center text-[#9E4535]">
            <ShieldQuestion className="w-6 h-6" />
          </div>
          <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A1615] leading-tight">
            Privacy Policy
          </h1>
        </div>
        <p className="caption-label mb-8 ml-16">Last updated: 18 September 2026</p>

        <div className="bg-white border border-[#D8CEB9] rounded-sm p-7 sm:p-9 space-y-7">
          <p className="font-body text-base sm:text-lg text-[#2E282A] leading-[1.85]">
            CurioVault (“we”, “us”) respects your privacy.
          </p>

          <Section title="What we collect">
            <p>
              We collect anonymous usage data via cookies and analytics to improve the
              collection. We do not collect personal data unless you contact us via email at{" "}
              <a href="mailto:curator@curiovault.co" className="text-[#9E4535] hover:underline">curator@curiovault.co</a>.
            </p>
          </Section>

          <Section title="Cookies and Advertising">
            <p>
              We use cookies to operate our site. We use Google AdSense and other third-party
              vendors to serve advertisements.
            </p>
            <p>
              Google, as a third-party vendor, uses cookies to serve ads on CurioVault. Google’s
              use of the DoubleClick DART cookie enables it to serve ads to our users based on
              their visit to CurioVault and other sites on the internet.
            </p>
            <p>
              Users may opt out of the use of the DART cookie by visiting the Google ad and
              content network Privacy Policy at{" "}
              <a
                href="https://policies.google.com/technologies/ads"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="privacy-google-dart-link"
                className="text-[#9E4535] hover:underline inline-flex items-center gap-1"
              >
                https://policies.google.com/technologies/ads <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </p>
            <p>
              You may also opt out of personalised advertising by visiting Google Ads Settings at{" "}
              <a
                href="https://www.google.com/settings/ads"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="privacy-google-ads-link"
                className="text-[#9E4535] hover:underline inline-flex items-center gap-1"
              >
                https://www.google.com/settings/ads <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </p>
            <p>
              Our advertising partners may include Google and other third-party ad networks.
              Please consult their respective Privacy Policies for more information on their
              practices.
            </p>
          </Section>

          <Section title="Analytics">
            <p>
              We use anonymised analytics (such as Google Analytics) to understand which
              curiosities are most visited.
            </p>
          </Section>

          <Section title="Your rights (UK GDPR)">
            <p>
              You have the right to request access to, or deletion of, any personal data you
              have provided to us via email. To exercise this, email{" "}
              <a href="mailto:curator@curiovault.co" className="text-[#9E4535] hover:underline">curator@curiovault.co</a>.
            </p>
          </Section>

          <Section title="Contact">
            <p>
              If you have questions about this policy, contact{" "}
              <a href="mailto:curator@curiovault.co" data-testid="privacy-contact-email" className="text-[#9E4535] hover:underline">curator@curiovault.co</a>.
            </p>
          </Section>

          <Section title="Affiliate / Copyright">
            <p>
              All illustrations are created for CurioVault. Text is original. External links
              are provided for verification and we are not responsible for external content.
            </p>
          </Section>
        </div>
      </div>
    </Layout>
  );
};

export default PrivacyPolicy;
